import { Box, TextField, Tooltip, styled } from '@mui/material';
import {
  ChangeEvent,
  FunctionComponent,
  useEffect,
  useRef,
  useState
} from 'react';
import { debounce } from 'lodash';
import formattedTimeInputSchema from './formatted-time-input-schema';
import { InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { formatHoursMinutes, formattedHoursMinutesToSeconds } from 'utils/date';
import { withTabbable } from '../Tabbable';
import { useTranslation } from 'react-i18next';
import { BLUE_DARK, DARK, PRIMARY, RED } from 'constants/colors';
import { lighten } from '@mui/material';

interface FormattedTimeInputProps {
  id?: string;
  isAbsent?: boolean;
  isEditable?: boolean;
  isCredit?: boolean;
  onCancel?: () => void;
  onFinished?: (durationInSeconds: number, stringValue: string) => void;
  value?: string;
}

enum TimeInputState {
  CHANGED,
  DEFAULT,
  SAVED
}

const TimeInput = styled(TextField, {
  shouldForwardProp: (p) => !['isAbsent'].includes(p as string)
})<{ isAbsent: boolean }>`
  font-family: SourceSansPro-Regular;
  line-height: 0;
  text-decoration: ${({ isAbsent }) => (isAbsent ? 'line-through' : 'none')};
  max-width: 45px;
`;

const TabbableTimeInput = withTabbable(TimeInput as FunctionComponent<any>);

const FormattedTimeInput = (props: FormattedTimeInputProps) => {
  const TIME_INPUT_STATE_CHANGE_DELAY = 2000; // ms

  const {
    id,
    isAbsent,
    isEditable,
    isCredit,
    onCancel,
    onFinished,
    value = '00u00'
  } = props;

  const { t } = useTranslation('common', {
    keyPrefix: 'activityPlanning.formattedTimeInput'
  });

  const schema = formattedTimeInputSchema();
  type Form = InferType<typeof schema>;

  const {
    control,
    formState: { isValid },
    reset,
    setValue
  } = useForm<Form>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      duration: value
    }
  });

  const isCancelled = useRef<boolean>(false);
  const [inputState, setInputState] = useState<TimeInputState>(
    TimeInputState.DEFAULT
  );

  const onHandleValueChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = event.target.value;
    const durationInSeconds = formattedHoursMinutesToSeconds(newValue);

    if (
      !newValue ||
      value === newValue ||
      !parseInt(newValue) ||
      isNaN(durationInSeconds)
    )
      return onHandleCancel();

    isValid && onFinished?.(durationInSeconds, newValue);
    setValue('duration', formatHoursMinutes(durationInSeconds));
    setInputState(TimeInputState.SAVED);
  };

  const onHandleCancel = () => {
    reset();
    setInputState(TimeInputState.DEFAULT);
    onCancel?.();
    isCancelled.current = false;
  };

  const getInputStateStyle = () => {
    const styles = {
      backgroundColor: 'transparent',
      borderRadius: '4px',
      transitionDuration: '500ms'
    };

    switch (inputState) {
      case TimeInputState.CHANGED:
        styles.backgroundColor = lighten(BLUE_DARK, 0.75);
        break;
      case TimeInputState.DEFAULT:
        styles.backgroundColor = 'transparent';
        break;
      case TimeInputState.SAVED:
        styles.backgroundColor = lighten(PRIMARY, 0.75);
        break;
    }

    return styles;
  };

  useEffect(() => {
    reset({ duration: value });
  }, [value]);

  useEffect(() => {
    if (
      inputState === TimeInputState.DEFAULT ||
      inputState === TimeInputState.CHANGED
    )
      return;

    setTimeout(
      () => setInputState(TimeInputState.DEFAULT),
      TIME_INPUT_STATE_CHANGE_DELAY
    );
  }, [inputState]);

  return (
    <Box position="relative">
      <Controller
        name="duration"
        control={control}
        render={({ field, fieldState }) => (
          <Tooltip
            enterNextDelay={500}
            disableFocusListener={!isEditable}
            disableHoverListener={!isEditable}
            placement="top"
            title={t('tooltip')}
          >
            <Box>
              <TabbableTimeInput
                {...field}
                id={id}
                disabled={!isEditable}
                type="text"
                variant="outlined"
                isAbsent={!!isAbsent}
                sx={{
                  '.MuiInputBase-input': {
                    textAlign: 'center',
                    padding: '1px 2px 0 2px',
                    fontSize: '13px',
                    color: isCredit ? RED : DARK,
                    ...getInputStateStyle(),
                    '&:disabled': {
                      WebkitTextFillColor: 'inherit',
                      cursor: 'text',
                      userSelect: 'auto',
                      '+ fieldset': {
                        borderColor: 'transparent'
                      }
                    }
                  },
                  'fieldset:not(:hover)': {
                    borderColor: 'transparent'
                  }
                }}
                error={!!fieldState.error}
                onChange={(event) => {
                  debounce(
                    () =>
                      setInputState(
                        event.target.value === value
                          ? TimeInputState.DEFAULT
                          : TimeInputState.CHANGED
                      ),
                    300
                  ).call(this);
                  field.onChange?.call(this, event);
                }}
                onFinished={(event) => {
                  if (isCancelled.current) return;
                  onHandleValueChange?.call(this, event);
                }}
                onCancel={(event) => {
                  if (event.target.value === value) return;
                  // Reset the value to the default value if escape is pressed
                  isCancelled.current = true;
                  onHandleCancel?.();
                }}
              />
            </Box>
          </Tooltip>
        )}
      />
    </Box>
  );
};

export default FormattedTimeInput;
